import get from "lodash/get";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Typography } from "@platform/design-system";

import {
  ActionCellConfig,
  ConfigTable,
  TextCellConfig,
  TextWithSubtextCellConfig,
  ValueFn,
} from "../../types";
import { brand } from "../../theme/brand";

const RenderCellTable = <T,>({
  column,
  row,
}: CellComponentProps<T>): React.JSX.Element => {
  const cellConfig = column.cellConfig;
  const CellComponent = Cells[cellConfig.type] || Cells["text"];
  return <CellComponent column={column} row={row} />;
};

type CellComponentProps<T> = {
  column: ConfigTable<T>;
  row: T;
};

const TextWithSubtextComponent = <T,>({
  column,
  row,
}: CellComponentProps<T>): React.JSX.Element => {
  const cellConfig = column.cellConfig as TextWithSubtextCellConfig<T>;

  const mainTextValue = get(row, column.field) as ValueFn;
  const mainText = cellConfig.formatter
    ? cellConfig.formatter(mainTextValue, row)
    : mainTextValue;

  const isHighlighted = cellConfig.highlightConditionField
    ? !!get(row, cellConfig.highlightConditionField)
    : false;

  const mainTextStyle = {
    color: isHighlighted ? brand("primary") : column.textColor,
    fontWeight: isHighlighted ? 600 : column.fontWeight,
  };

  let subTextValue: ValueFn = null;
  if (cellConfig.subtextField) {
    subTextValue = get(row, cellConfig.subtextField) as ValueFn;
  }
  if (cellConfig.subtextFormatter) {
    subTextValue = cellConfig.subtextFormatter(subTextValue, row);
  }

  const showSubtext = cellConfig.subtextCondition
    ? cellConfig.subtextCondition(subTextValue, row)
    : !!subTextValue;

  const subtextStyle = {
    fontSize: cellConfig.inline ? 12 : undefined,
    color: cellConfig.subtextColor || "neutrals.neutral_700",
    fontStyle: cellConfig.subtextFontStyle || "normal",
  };

  const formattedSubtext = cellConfig.subtextLabel || subTextValue;

  if (cellConfig.inline) {
    return (
      <Typography sx={mainTextStyle} data-testid="ds-text-with-subtext-cell">
        {mainText}{" "}
        {showSubtext && (
          <Typography sx={subtextStyle} component="span">
            {formattedSubtext}
          </Typography>
        )}
      </Typography>
    );
  }
  return (
    <div data-testid="ds-text-with-subtext-cell">
      <Typography variant="body-large" sx={mainTextStyle}>
        {mainText}
      </Typography>
      {showSubtext && (
        <Typography variant="body-medium" sx={subtextStyle}>
          {formattedSubtext}
        </Typography>
      )}
    </div>
  );
};

const ActionComponent = <T,>({
  column,
  row,
}: CellComponentProps<T>): React.JSX.Element => {
  const cellConfig = column.cellConfig as ActionCellConfig<T>;

  return (
    <IconButton
      data-testid="ds-action-cell"
      aria-label="edit"
      sx={{ color: brand("primary") }}
      onClick={(e) => {
        e.stopPropagation();
        cellConfig.onClick(row);
      }}
    >
      {cellConfig.icon || <EditIcon />}
    </IconButton>
  );
};

const TextComponent = <T,>({
  column,
  row,
}: CellComponentProps<T>): React.JSX.Element => {
  const value = get(row, column.field) as ValueFn;
  const cellConfig = column.cellConfig as TextCellConfig<T>;
  const formattedValue = cellConfig.formatter
    ? cellConfig.formatter(value, row)
    : value;
  return (
    <Typography
      data-testid="ds-text-cell"
      variant="body-large"
      sx={{
        color: column.textColor,
        fontWeight: column.fontWeight,
      }}
    >
      {formattedValue}
    </Typography>
  );
};

const Cells: {
  [cellName: string]: <T>(props: CellComponentProps<T>) => React.JSX.Element;
} = {
  text: TextComponent,
  action: ActionComponent,
  textWithSubtext: TextWithSubtextComponent,
};

export { RenderCellTable };
